import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import styles from "../styles/theme";
import { CERT_COLORS } from "../utils/constants";
import { certExpiryStatus, physicalExpiryStatus, calcJobMatch, normalizeJob, techHasCert } from "../utils/helpers";

const DOC_TYPE_LABELS = {
  resume: 'Resume',
  hazwoper_8hr: 'HAZWOPER 8-Hour Refresher Cert',
  hazwoper_40hr: 'HAZWOPER 40-Hour Cert',
  uxo_eod_cert: 'UXO/EOD Certification',
  physical: 'Physical Exam Cert',
  first_aid_cpr: 'First Aid/CPR Cert',
  logbook: 'Signed Logbook',
  quality_cert: 'Quality Credential Certificate',
  osha_10_construction: '10-Hour OSHA Construction Certificate',
};

export default function TechDetail({ tech, user, onBack, accountType, isPaidEmployer, navigate }) {
  const [contactView, setContactView] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', company: '', message: '' });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState('');

  // Unlock state
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [employerProfileId, setEmployerProfileId] = useState(null);
  const [unlockBalance, setUnlockBalance] = useState(null);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [showOverageConfirm, setShowOverageConfirm] = useState(false);

  // Documents state
  const [techDocs, setTechDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);

  // Job match state
  const [jobMatches, setJobMatches] = useState([]);

  const isEmployer = accountType === 'employer';
  const isFreeEmployer = isEmployer && !isPaidEmployer;
  const canContact = isEmployer && isPaidEmployer; // only paid employers can contact techs

  // Fetch employer's active jobs for match display
  useEffect(() => {
    if (!isEmployer || !user) return;
    supabase.from('job_posts').select('*').eq('user_id', user.id).eq('status', 'published')
      .then(({ data }) => {
        const jobs = (data || []).map(normalizeJob);
        setJobMatches(jobs.map(job => calcJobMatch(tech, job)));
      });
  }, [isEmployer, user, tech.id]); // eslint-disable-line

  // Fetch employer profile id, unlock status, and documents in one effect
  useEffect(() => {
    if (!isEmployer || !isPaidEmployer || !user) return;

    (async () => {
      const { data: ep } = await supabase.from('employer_profiles')
        .select('id').eq('user_id', user.id).single();
      if (!ep) return;
      setEmployerProfileId(ep.id);

      // Check if already unlocked
      const { data: unlock } = await supabase.from('contact_unlocks')
        .select('id').eq('employer_id', ep.id).eq('tech_profile_id', tech.id).single();
      if (unlock) {
        setIsUnlocked(true);
        // Fetch documents immediately when we know the tech is unlocked
        fetchTechDocs();
      }

      // Fetch balance
      const { data: bal } = await supabase.from('unlock_balance')
        .select('*').eq('employer_id', ep.id).single();
      if (bal) setUnlockBalance(bal);
    })();
  }, [isEmployer, isPaidEmployer, user, tech.id]); // eslint-disable-line

  const handleUnlock = async () => {
    if (!employerProfileId) return;
    if (isUnlocked) return;

    if (unlockBalance && unlockBalance.current_balance <= 0) {
      setShowOverageConfirm(true);
      return;
    }
    await performUnlock(false);
  };

  // One RPC now does the insert, the profile snapshot and the balance move in
  // a single transaction (migration 078). The snapshot has to be written
  // server-side to be worth anything, and the client can no longer decide
  // whether a given unlock counts as an overage — the server decides that
  // from the actual balance.
  const performUnlock = async (isOverage) => {
    setUnlockLoading(true);
    try {
      const { data, error } = await supabase.rpc('unlock_tech_contact', {
        p_employer_id: employerProfileId,
        p_tech_profile_id: tech.id,
        p_accept_overage: !!isOverage,
      });

      if (error) {
        // The server refused because the balance is gone and no overage was
        // authorised. Surface the confirm dialog instead of failing silently.
        if ((error.message || '').includes('insufficient_balance')) {
          setShowOverageConfirm(true);
        }
        setUnlockLoading(false);
        return;
      }

      if (data?.was_overage) {
        // Report overage to Stripe metered billing (fire and forget)
        supabase.functions.invoke('report-overage-usage', {
          body: { employer_profile_id: employerProfileId },
        }).catch(() => {});
      } else if (data?.new_balance != null) {
        setUnlockBalance(prev => prev ? { ...prev, current_balance: data.new_balance } : prev);
      }

      setIsUnlocked(true);
      setShowOverageConfirm(false);

      // Fetch documents now that this tech is unlocked
      fetchTechDocs();

      // Fire notification
      supabase.functions.invoke('send-unlock-notification', {
        body: { tech_profile_id: tech.id },
      }).catch(() => {});
    } finally {
      setUnlockLoading(false);
    }
  };

  const fetchTechDocs = async () => {
    setDocsLoading(true);
    // Fetch from new tech_documents table
    const { data, error } = await supabase
      .from('tech_documents')
      .select('*')
      .eq('tech_profile_id', tech.id)
      .order('uploaded_at', { ascending: false });
    if (error) {
      console.error('Failed to fetch tech documents:', error);
    }
    const newDocs = (data || []).map(d => ({
      id: d.id,
      label: DOC_TYPE_LABELS[d.document_type] || d.document_type,
      file_path: d.file_path,
      file_name: d.file_name,
      uploaded_at: d.uploaded_at,
      source: 'new',
    }));

    // Build docs from old profile columns (only if not already in new system)
    const newTypes = new Set((data || []).map(d => d.document_type));
    const oldDocs = [];
    if (tech.resumePath && !newTypes.has('resume')) {
      oldDocs.push({ id: 'old-resume', label: 'Resume', file_path: tech.resumePath, file_name: tech.resumePath.split('/').pop(), source: 'old' });
    }
    if (tech.hazwoper8CertPath && !newTypes.has('hazwoper_8hr')) {
      oldDocs.push({ id: 'old-hazwoper8', label: 'HAZWOPER 8-Hour Refresher Cert', file_path: tech.hazwoper8CertPath, file_name: tech.hazwoper8CertPath.split('/').pop(), source: 'old' });
    }
    if (tech.physicalCertPath && !newTypes.has('physical')) {
      oldDocs.push({ id: 'old-physical', label: 'Physical Exam Cert', file_path: tech.physicalCertPath, file_name: tech.physicalCertPath.split('/').pop(), source: 'old' });
    }
    if (tech.certPaths && tech.certPaths.length > 0) {
      tech.certPaths.forEach((p, i) => {
        oldDocs.push({ id: `old-cert-${i}`, label: `Certification Document`, file_path: p, file_name: p.split('/').pop(), source: 'old' });
      });
    }

    setTechDocs([...newDocs, ...oldDocs]);
    setDocsLoading(false);
  };

  const handleDownloadDoc = async (doc) => {
    const { data, error } = await supabase.storage
      .from('uxo-uploads')
      .createSignedUrl(doc.file_path, 300);
    if (error) {
      console.error('Signed URL failed:', error);
      // Fallback: try direct download
      const { data: dlData, error: dlError } = await supabase.storage
        .from('uxo-uploads')
        .download(doc.file_path);
      if (dlError) {
        console.error('Download failed:', dlError);
        alert('Unable to download this document. The file may not be accessible.');
        return;
      }
      const url = URL.createObjectURL(dlData);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      a.click();
      URL.revokeObjectURL(url);
      return;
    }
    if (data?.signedUrl) {
      window.open(data.signedUrl, '_blank');
    }
  };

  const handleContact = async () => {
    if (!contactForm.message) {
      setContactError('Message is required.');
      return;
    }
    setContactSubmitting(true);
    setContactError('');
    const { error } = await supabase.from('messages').insert({
      employer_profile_id: employerProfileId,
      tech_profile_id: tech.id,
      sender_type: 'employer',
      message: contactForm.message,
    });
    if (!error) {
      // Notify tech via email (fire and forget)
      supabase.functions.invoke('send-message-notification', {
        body: { tech_profile_id: tech.id, employer_profile_id: employerProfileId },
      }).catch(() => {});
    }
    setContactSubmitting(false);
    if (error) setContactError('Something went wrong. Please try again.');
    else setContactSuccess(true);
  };

  // For free employers, mask location to state/region only
  const displayLocation = isFreeEmployer && tech.location
    ? tech.location.split(',').pop()?.trim() || tech.location
    : (isEmployer && isPaidEmployer && isUnlocked ? tech.location : (
      tech.location?.split(',').pop()?.trim() || tech.location
    ));

  return (
    <div style={styles.detailWrap} data-detail-wrap>
      <button style={styles.backBtn} data-back-btn onClick={onBack}>{"\u2190"} Back to Techs</button>
      <div style={styles.detailCard} data-detail-card>
        <div style={styles.detailHeader}>
          <div>
            <h2 style={styles.detailTitle}>{tech.name}</h2>
            <div style={{ fontSize: 13, color: '#7a7570', marginBottom: 6 }}>
              <span>{"\uD83D\uDCCD"} {displayLocation}</span>
              <span>{" \u00B7 "}{"\u2708\uFE0F"} {tech.travel}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
              {/* UXO hours badge */}
              {(() => {
                const isVerified = tech.logbookVerificationStatus === 'verified';
                return (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontSize: 12, fontWeight: 'bold', padding: '4px 12px', borderRadius: 20,
                    background: isVerified ? '#1a1408' : '#111316',
                    border: `1px solid ${isVerified ? '#d9770644' : '#333'}`,
                    color: isVerified ? '#d97706' : '#7a7570',
                  }}>
                    {"\u23F1"} {tech.uxoHours} UXO hrs
                    {isVerified ? <span>{"\u2713"} Hours Documented</span> : <span style={{ fontWeight: 'normal' }}>(self-reported)</span>}
                  </span>
                );
              })()}
              {/* Supervisory hours badge */}
              {(tech.logbookVerifiedSupervisoryHours != null || tech.supervisoryHours > 0) && (() => {
                // Status gates the badge, not the stored figure: returning a logbook
                // for correction leaves the previously documented hours on the row,
                // and those must not keep reading as documented while the submission
                // is back with the tech.
                const isVerified = tech.logbookVerifiedSupervisoryHours != null
                  && tech.logbookVerificationStatus === 'verified';
                const hrs = isVerified ? tech.logbookVerifiedSupervisoryHours : tech.supervisoryHours;
                return (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontSize: 12, fontWeight: 'bold', padding: '4px 12px', borderRadius: 20,
                    background: isVerified ? '#0a1a1f' : '#111316',
                    border: `1px solid ${isVerified ? '#2dd4bf44' : '#333'}`,
                    color: isVerified ? '#2dd4bf' : '#7a7570',
                  }}>
                    {"\uD83D\uDC54"} {hrs.toLocaleString()} supervisory hrs
                    {isVerified ? <span>{"\u2713"} Supervisory Hours Documented</span> : <span style={{ fontWeight: 'normal' }}>(self-reported)</span>}
                  </span>
                );
              })()}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ ...styles.availBadge, background: "#1a4a2e", color: "#4ade80" }}>{"\u25CF"} Open to Work</div>
            {tech.logbookVerificationStatus === 'verified' && (
              <div style={{
                fontSize: 11, padding: '3px 10px', borderRadius: 12,
                background: '#1a1408', color: '#d97706', border: '1px solid #d9770633',
                fontWeight: 'bold',
              }}>{"\u2713"} Hours Documented</div>
            )}
            {tech.hasLogbook && tech.logbookVerificationStatus !== 'verified' && (
              <div style={{
                fontSize: 11, padding: '3px 10px', borderRadius: 12,
                background: '#1a1408', color: '#d97706', border: '1px solid #d9770633',
              }}>{"\uD83D\uDCD6"} Logbook Available</div>
            )}
            {tech.qualityCredentialStatus === 'verified' && (
              <div style={{
                fontSize: 11, padding: '3px 10px', borderRadius: 12,
                background: '#1a1408', color: '#d97706', border: '1px solid #d9770633',
                fontWeight: 'bold',
              }}>QC {"\u2713"}</div>
            )}
            {isEmployer && isPaidEmployer && isUnlocked && (
              <div style={{
                fontSize: 11, padding: '3px 10px', borderRadius: 12,
                background: '#0a2a1a', color: '#4ade80', border: '1px solid #4ade8033',
              }}>{"\u2713"} Unlocked</div>
            )}
          </div>
        </div>
        <div style={styles.divider} />

        {/* Verified hours detail */}
        {tech.logbookVerificationStatus === 'verified' && tech.logbookVerifiedHours != null && (
          <div style={{
            background: '#1a1408', border: '1px solid #d9770644', borderRadius: 8,
            padding: '14px 18px', marginBottom: 16,
          }}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={{ fontSize: 16, fontWeight: 'bold', color: '#d97706' }}>
                {tech.logbookVerifiedHours.toLocaleString()} hrs {"\u2713"} Hours Documented by UXOHire
              </span>
              <span style={{ fontSize: 13, color: '#7a7570' }}>
                {tech.uxoHours} hrs (self-reported)
              </span>
            </div>
            {tech.logbookVerifiedSupervisoryHours != null && (
              <div style={{ display: 'flex', gap: 16, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 'bold', color: '#d97706' }}>
                  {tech.logbookVerifiedSupervisoryHours.toLocaleString()} supervisory hrs {"\u2713"} Supervisory Hours Documented by UXOHire
                </span>
                {tech.supervisoryHours > tech.logbookVerifiedSupervisoryHours && (
                  <span style={{ fontSize: 13, color: '#7a7570' }}>
                    {tech.supervisoryHours.toLocaleString()} supervisory hrs (self-reported)
                  </span>
                )}
              </div>
            )}
            <p style={{ fontSize: 11, color: '#7a7570', margin: 0, lineHeight: 1.5 }}>
              UXOHire reviews submitted logbook hours for the signature and attestation of a current or former supervisor. UXOHire does not independently audit or guarantee the accuracy of reported hours.
            </p>
          </div>
        )}

        {/* Quality credential detail */}
        {tech.qualityCredential && tech.qualityCredential !== 'None' && (
          <div style={{
            background: '#111316', border: '1px solid #1e2022', borderRadius: 8,
            padding: '12px 18px', marginBottom: 16,
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 14, color: '#e8e4dc' }}>Quality Credential: {tech.qualityCredential}</span>
            {tech.qualityCredentialStatus === 'verified' ? (
              <span style={{
                background: '#1a1408', color: '#d97706', fontSize: 11,
                padding: '2px 8px', borderRadius: 10, fontWeight: 'bold',
                border: '1px solid #d9770633',
              }}>{"\u2713"} Documented</span>
            ) : (
              <span style={{ fontSize: 12, color: '#7a7570' }}>(not documented)</span>
            )}
          </div>
        )}

        {/* Employer verification disclaimer */}
        {(tech.logbookVerificationStatus === 'verified' || tech.qualityCredentialStatus === 'verified') && (
          <div style={{
            background: '#111316', border: '1px solid #1e2022', borderRadius: 8,
            padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 8,
          }}>
            <span style={{ fontSize: 13, flexShrink: 0 }}>{"\u26A0\uFE0F"}</span>
            <p style={{ fontSize: 11, color: '#7a7570', margin: 0, lineHeight: 1.6 }}>
              UXOHire reviews documents for format and supervisor attestation — we do not verify authenticity, document legitimacy, or the accuracy of any information. Employers are responsible for independent verification prior to hiring.{" "}
              <a href="/terms#verification-disclaimer" style={{ color: '#d97706', textDecoration: 'underline' }}>Learn more</a>
            </p>
          </div>
        )}

        {/* Job Match banners — prominent, at top of profile */}
        {isEmployer && jobMatches.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
            {jobMatches.map(match => (
              <div key={match.jobId} style={{
                borderRadius: 8, padding: '16px 20px', overflow: 'hidden',
                background: match.hasNoRequirements ? '#1e293b' : match.meetsAllRequired ? '#14532d' : '#451a03',
                border: `2px solid ${match.hasNoRequirements ? '#334155' : match.meetsAllRequired ? '#16a34a' : '#d97706'}`,
              }}>
                {/* Banner header */}
                <div style={{
                  fontSize: 16, fontWeight: 'bold', marginBottom: match.hasNoRequirements ? 0 : 8,
                  color: '#fff',
                }}>
                  {match.hasNoRequirements
                    ? `No requirements set for ${match.jobTitle}`
                    : match.meetsAllRequired
                    ? `\u2713 Qualifies for ${match.jobTitle}`
                    : `Missing ${match.missingRequired.length} requirement${match.missingRequired.length !== 1 ? 's' : ''} for ${match.jobTitle}`
                  }
                </div>

                {/* Missing certs list (amber banner only) */}
                {!match.meetsAllRequired && match.missingRequired.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                    {match.missingRequired.map(cert => (
                      <span key={cert} style={{
                        fontSize: 12, padding: '3px 10px', borderRadius: 4,
                        background: '#7c2d12', color: '#fbbf24', fontWeight: 'bold',
                      }}>{"\u2717"} {cert}</span>
                    ))}
                  </div>
                )}

                {/* Required certs breakdown */}
                {match.required.length > 0 && (
                  <div style={{ marginBottom: 6 }}>
                    <div style={{ fontSize: 12, color: match.meetsAllRequired ? '#bbf7d0' : '#fde68a', marginBottom: 4, fontWeight: 'bold' }}>Required Certs</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {match.required.map(cert => {
                        const has = techHasCert(tech, cert);
                        return (
                          <span key={cert} style={{
                            fontSize: 11, padding: '3px 10px', borderRadius: 4, fontWeight: 600,
                            background: has ? '#166534' : '#7f1d1d',
                            color: has ? '#bbf7d0' : '#fecaca',
                          }}>{has ? "\u2713" : "\u2717"} {cert}</span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Preferred certs */}
                {match.preferred.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ fontSize: 12, color: match.meetsAllRequired ? '#bbf7d0' : '#fde68a', marginBottom: 4, fontWeight: 'bold' }}>
                      Preferred Certs — {match.preferredPct}% match
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {match.preferred.map(cert => {
                        const has = techHasCert(tech, cert);
                        return (
                          <span key={cert} style={{
                            fontSize: 11, padding: '3px 10px', borderRadius: 4, fontWeight: 600,
                            background: has ? '#166534' : '#1e293b',
                            color: has ? '#bbf7d0' : '#94a3b8',
                          }}>{has ? "\u2713" : "\u2717"} {cert}</span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Upgrade banner for free employers */}
        {isFreeEmployer && (
          <div style={{
            background: '#1a1408', border: '1px solid #d97706', borderRadius: 8,
            padding: '16px 20px', marginBottom: 20, textAlign: 'center',
          }}>
            <div style={{ color: '#d97706', fontWeight: 'bold', fontSize: 15, marginBottom: 6 }}>
              {"\uD83D\uDD12"} Unlock Profile — Subscribe to access
            </div>
            <div style={{ color: '#9a9490', fontSize: 13, lineHeight: 1.5, marginBottom: 12 }}>
              Subscribe to unlock technician profiles, view direct contact information, and send messages.
            </div>
            <button style={{
              background: '#d97706', border: 'none', color: '#0d0f10', padding: '10px 24px',
              fontSize: 14, fontWeight: 'bold', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
            }} onClick={() => navigate && navigate('/employer-dashboard', { state: { openTab: 'account' } })}>
              Upgrade Now — from $1,199/mo
            </button>
          </div>
        )}

        <h3 style={styles.sectionLabel} data-section-label>Certifications</h3>
        <div style={styles.certTags} data-cert-tags>
          {tech.dodCerts.map(c => <span key={c} style={{ ...styles.certTag, background: CERT_COLORS[c] || "#333" }} data-cert-tag>{c}</span>)}
        </div>
        <h3 style={styles.sectionLabel} data-section-label>Qualifications</h3>
        <div style={styles.qualGrid} data-qual-grid>
          {(() => {
            const STATUS_BADGE = {
              active:   { bg: '#0a2a1a', color: '#4ade80', border: '#4ade8033', label: 'Active' },
              expiring: { bg: '#2a1a08', color: '#d97706', border: '#d9770633', label: 'Expiring Soon' },
              expired:  { bg: '#2a0a0a', color: '#f87171', border: '#f8717133', label: 'Expired' },
              unknown:  { bg: '#1a1c1e', color: '#6a6660', border: '#2a2c2e',   label: 'No Date' },
            };
            const badge = (status) => {
              const s = STATUS_BADGE[status];
              return (
                <span style={{
                  fontSize: 9, padding: '2px 6px', borderRadius: 4, marginLeft: 6,
                  background: s.bg, color: s.color, border: `1px solid ${s.border}`,
                }}>{s.label}</span>
              );
            };
            const quals = [
              { label: "HAZWOPER 40-Hour", val: tech.hazwoper40 },
              { label: "8-Hour Refresher", val: tech.hazwoper8, status: tech.hazwoper8 ? certExpiryStatus(tech.hazwoper8Expiry) : null },
              { label: "OSHA 10-Hr Construction", val: tech.osha10Construction },
              { label: "Current Physical", val: tech.physicalCurrent, status: tech.physicalCurrent ? physicalExpiryStatus(tech.physicalDate) : null },
              { label: "First Aid/CPR", val: tech.firstAidCpr, status: tech.firstAidCpr ? certExpiryStatus(tech.firstAidCprExpiry) : null },
              { label: "Quality Credential", val: !!tech.qualityCredential && tech.qualityCredential !== 'None', extra: tech.qualityCredential && tech.qualityCredential !== 'None' ? tech.qualityCredential : null },
              { label: "EOD Background", val: tech.militaryEod },
              { label: "Security Clearance", val: tech.clearance, extra: tech.clearance ? tech.clearanceLevel : null },
              { label: "Dive Certified", val: tech.diveCert },
              { label: "Driver's License", val: tech.driversLicense },
              { label: "CDL", val: tech.cdl },
            ];
            if (tech.stateLicenseExpiry) {
              quals.push({ label: "State License", val: true, status: certExpiryStatus(tech.stateLicenseExpiry) });
            }
            return quals.map(({ label, val, extra, status }) => (
              <div key={label} style={styles.qualItem}>
                <span style={styles.qualLabel}>{label}</span>
                <span style={val ? styles.qualYes : styles.qualNo}>
                  {val ? (extra ? `\u2713 ${extra}` : "\u2713 Yes") : "\u2717 No"}
                  {val && status && badge(status)}
                </span>
              </div>
            ));
          })()}
        </div>

        {!isFreeEmployer && (
          <>
            <h3 style={styles.sectionLabel} data-section-label>Summary</h3>
            <p style={styles.detailDesc}>{tech.summary}</p>
          </>
        )}

        {/* ── Unlocked contact info display ── */}
        {isEmployer && isPaidEmployer && isUnlocked && (
          <div style={{
            background: '#0a2a1a', border: '1px solid #4ade8033', borderRadius: 8,
            padding: '18px 20px', marginTop: 16, marginBottom: 16,
          }}>
            <h3 style={{ ...styles.sectionLabel, marginTop: 0, color: '#4ade80' }}>Contact Information</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {tech.email && (
                <div style={{ fontSize: 14, color: '#e8e4dc' }}>
                  <span style={{ color: '#7a7570', marginRight: 8 }}>Email:</span>
                  <a href={`mailto:${tech.email}`} style={{ color: '#d97706', textDecoration: 'none' }}>{tech.email}</a>
                </div>
              )}
              {tech.phone && (
                <div style={{ fontSize: 14, color: '#e8e4dc' }}>
                  <span style={{ color: '#7a7570', marginRight: 8 }}>Phone:</span>
                  <a href={`tel:${tech.phone}`} style={{ color: '#d97706', textDecoration: 'none' }}>{tech.phone}</a>
                </div>
              )}
              {tech.location && (
                <div style={{ fontSize: 14, color: '#e8e4dc' }}>
                  <span style={{ color: '#7a7570', marginRight: 8 }}>Location:</span>
                  {tech.location}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Documents section (after unlock) ── */}
        {isEmployer && isPaidEmployer && isUnlocked && (
          <div style={{
            background: '#111316', border: '1px solid #2a2c2e', borderRadius: 8,
            padding: '18px 20px', marginTop: 16, marginBottom: 16,
          }}>
            <h3 style={{ ...styles.sectionLabel, marginTop: 0 }}>Documents</h3>
            {docsLoading && <p style={{ color: '#7a7570', fontSize: 13 }}>Loading documents...</p>}
            {!docsLoading && techDocs.length === 0 && (
              <p style={{ color: '#7a7570', fontSize: 13, margin: 0 }}>
                This technician has not uploaded any documents yet.
              </p>
            )}
            {!docsLoading && techDocs.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {techDocs.map(doc => (
                  <div key={doc.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: '#0d0f10', border: '1px solid #1e2022', borderRadius: 6,
                    padding: '10px 14px', flexWrap: 'wrap', gap: 8,
                  }}>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <div style={{ fontSize: 13, fontWeight: 'bold', color: '#e8e4dc' }}>
                        {doc.label}
                      </div>
                      <div style={{ fontSize: 11, color: '#7a7570' }}>
                        {doc.uploaded_at ? `Uploaded ${new Date(doc.uploaded_at).toLocaleDateString()}` : doc.file_name}
                      </div>
                    </div>
                    <button onClick={() => handleDownloadDoc(doc)} style={{
                      background: '#d97706', border: 'none', color: '#0d0f10',
                      padding: '5px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                      fontWeight: 'bold', fontFamily: 'inherit', whiteSpace: 'nowrap',
                    }}>
                      Download
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Unlock button for paid employers ── */}
        {isEmployer && isPaidEmployer && !isUnlocked && (
          <div style={{
            background: '#111316', border: '1px solid #2a2c2e', borderRadius: 8,
            padding: '20px 24px', marginTop: 16, textAlign: 'center',
          }}>
            <div style={{ color: '#9a9490', fontSize: 13, marginBottom: 12 }}>
              Unlock this profile to view direct contact information.
              {unlockBalance && unlockBalance.current_balance > 0 && (
                <span style={{ color: '#7a7570' }}> ({unlockBalance.current_balance} unlocks remaining)</span>
              )}
              {unlockBalance && unlockBalance.current_balance === 0 && (
                <span style={{ color: '#d97706' }}> (Overage: $20)</span>
              )}
            </div>
            <button style={styles.btnPrimary} onClick={handleUnlock} disabled={unlockLoading}>
              {unlockLoading ? 'Unlocking...' : '\uD83D\uDD13 Unlock Profile'}
            </button>
          </div>
        )}

        {/* ── Overage confirmation ── */}
        {showOverageConfirm && (
          <div style={{
            background: '#1a1408', border: '1px solid #d97706', borderRadius: 8,
            padding: '18px 20px', marginTop: 12, textAlign: 'center',
          }}>
            <p style={{ color: '#9a9490', fontSize: 14, lineHeight: 1.6, margin: '0 0 14px' }}>
              You have used all {unlockBalance?.monthly_allowance || 10} unlocks this month.
              You can upgrade your plan for more unlocks, or use an overage unlock.
              This will be added to your next invoice.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button style={{
                background: 'transparent', border: '1px solid #333', color: '#e8e4dc',
                padding: '8px 18px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13,
              }} onClick={() => setShowOverageConfirm(false)}>Cancel</button>
              <button style={styles.btnPrimary} onClick={() => performUnlock(true)} disabled={unlockLoading}>
                {unlockLoading ? 'Unlocking...' : 'Yes, Unlock'}
              </button>
            </div>
          </div>
        )}

        {/* Contact section — free employers see subscribe prompt */}
        {isFreeEmployer && (
          <div style={{
            background: '#111316', border: '1px solid #2a2c2e', borderRadius: 8,
            padding: '20px 24px', marginTop: 20, textAlign: 'center',
          }}>
            <div style={{ color: '#9a9490', fontSize: 14 }}>
              {"\uD83D\uDD12"} Subscribe to contact this technician
            </div>
          </div>
        )}

        {/* Contact form — only for paid employers with employer profile */}
        {canContact && employerProfileId && !contactView && !contactSuccess && (
          <button style={styles.btnPrimary} data-btn-primary onClick={() => {
            setContactForm({ message: '' });
            setContactError('');
            setContactView(true);
          }}>Contact This Tech</button>
        )}

        {contactSuccess && (
          <div style={{ background: '#1a4a2e', border: '1px solid #4ade80', borderRadius: 8, padding: '16px 20px', color: '#4ade80', fontSize: 14 }}>
            {"\u2705"} Message sent! The technician will be notified.
          </div>
        )}

        {contactView && !contactSuccess && canContact && employerProfileId && (
          <div style={{ marginTop: 16, background: '#0d0f10', border: '1px solid #2a2c2e', borderRadius: 8, padding: '20px 24px' }}>
            <h3 style={{ ...styles.sectionLabel, marginTop: 0 }}>Contact {tech.name}</h3>
            <div style={styles.formFields} data-form-fields>
              <label style={styles.label}>Message</label>
              <textarea style={styles.textarea} value={contactForm.message} onChange={e => setContactForm(f => ({ ...f, message: e.target.value }))} placeholder="Describe the role, project, and why you'd like to connect..." />
              {contactError && <div style={styles.errorMsg}>{"\u26A0\uFE0F"} {contactError}</div>}
              <div style={styles.formRow} data-form-row>
                <button style={styles.btnSecondary} data-btn-secondary onClick={() => { setContactView(false); setContactError(''); }}>Cancel</button>
                <button style={styles.btnPrimary} data-btn-primary onClick={handleContact} disabled={contactSubmitting}>
                  {contactSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
